import React, { useState } from 'react';

import TpDialog from '../shared/tp_dialog';
import { discard_guard_text } from '../shared/dialog_logic';
import { post_close_switch } from '../api';
import {
  confirm_label,
  confirm_text,
  switch_error,
  switch_payload,
  switch_reason_error,
  switch_view
} from './adapters';

/* "Month close: ON/OFF" in every page header. */

// shown: the state the page showed when the dialog opened, fixed so a re-read can't flip the target.
const CloseSwitchDialog = ({ shown, on_close, on_changed, on_stale }) => {
  const [target] = useState(!shown);
  const [expected] = useState(shown);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [server_error, setServerError] = useState('');
  const [stale, setStale] = useState(false);
  const error = switch_reason_error(reason);
  const submit_off = submitting || stale;

  const submit = () => {
    setTouched(true);
    if (error || submit_off) return;
    setSubmitting(true);
    setServerError('');
    post_close_switch(switch_payload(target, expected, reason))
      .then((d) => {
        setSubmitting(false);
        on_changed(d);
        on_close();
      })
      .catch((e) => {
        const r = switch_error(e);
        setSubmitting(false);
        setServerError(r.text);
        if (r.reload) {
          setStale(true);
          on_stale();
        }
      });
  };

  return (
    <TpDialog
      title={confirm_label(target)}
      on_close={on_close}
      busy={submitting}
      guard_text={discard_guard_text(reason)}
    >
      <div className="tp-modal">
        <div className="tp-ctx">{confirm_text(target)}</div>
        <div className="tp-field">
          <label htmlFor="tp-switch-reason">Reason (required, recorded in the audit trail)</label>
          <textarea
            id="tp-switch-reason"
            className="tp-textarea"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="Why is month close being switched?"
          />
          {touched && error ? (
            <span className="tp-neg" role="alert">
              {error}
            </span>
          ) : null}
          {server_error ? (
            <span className="tp-neg" role="alert">
              {server_error}
            </span>
          ) : null}
        </div>
        <div className="tp-modal-ft">
          {/* aria-disabled, not disabled: disabling the focused button drops focus. */}
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={submitting ? undefined : on_close}
            aria-disabled={submitting ? 'true' : undefined}
          >
            Cancel
          </button>
          <button
            type="button"
            className={target ? 'tp-btn' : 'tp-btn tp-btn-danger'}
            onClick={submit_off ? undefined : submit}
            aria-disabled={submit_off ? 'true' : undefined}
          >
            {submitting ? 'Saving…' : confirm_label(target)}
          </button>
        </div>
      </div>
    </TpDialog>
  );
};

const CloseSwitchChip = ({ data, on_changed, on_stale }) => {
  const [open, setOpen] = useState(false);
  const view = switch_view(data);
  if (!view) return null;
  const cls = `tp-switch tp-switch-${view.tone}`;
  if (!view.can_toggle) {
    return (
      <span className={cls} title={view.title}>
        {view.label}
      </span>
    );
  }
  return (
    <>
      <button
        type="button"
        className={`${cls} tp-switch-btn`}
        title={`${view.title} Click to change it.`}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        {view.label}
      </button>
      {open ? (
        <CloseSwitchDialog
          shown={view.enabled}
          on_close={() => setOpen(false)}
          on_changed={on_changed}
          on_stale={on_stale}
        />
      ) : null}
    </>
  );
};

export default CloseSwitchChip;
